import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import {BehaviorSubject, combineLatest, interval, Observable, of} from 'rxjs';
import { distinctUntilChanged, filter, map, take, tap } from 'rxjs/operators';
import { Position } from '../models/api/vehicle-position.model';
import { environment } from '../../environments/environment';
import { AuthService } from '../core/auth.service';
import { AppSelector } from '../core/state/app-selector';
import { DSNotification } from '../models/notification';
import {
  UserNotifications,
  VehiclesNotifications,
} from '../models/notifications';

@Injectable()
export class NotificationsService {
  private deviceNotificationsUrl =
    environment.baseUrl + environment.api + 'dslocate/messages/etoll';
  private dslocateUserNotificationsUrl =
    environment.baseUrl + environment.api + 'dslocate/messages';

  private userNotifications: UserNotifications = {
    notifications: [],
    unread: 0,
  };
  _notifications$ = new BehaviorSubject<UserNotifications>({
    notifications: [],
    unread: 0,
  });

  private notificationPerVehicle: VehiclesNotifications = {};
  _notificationsPerVehicle$ = new BehaviorSubject<VehiclesNotifications>({});

  notificationsGetInterval: Observable<any>;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private appSelector: AppSelector
  ) {
    this.notificationsGetInterval = interval(30000).pipe(
      filter(() => this.authService.isValidToken()),
      tap(() => {
        this.loadLastPerVehicleNotification().subscribe();

        this.loadUserNotifications().subscribe();
      })
    );
    this.notificationsGetInterval.subscribe();
  }

  loadVehiclesNotifications(
    vehicleId: string,
    skip: number = 0,
    limit: number = 10
  ): Observable<VehiclesNotifications> {
    if (this.authService.isDSLocateUser()) {
      return combineLatest(
        this.http
          .get(
            this.deviceNotificationsUrl +
              `/${vehicleId}?start=${skip}&count=${limit}`,
            {
              headers: this.authService.getJwtAuthHeaders(),
            }
          )
          .pipe(
            map((vehicleNotifications: DSNotification[]) => {
              return vehicleNotifications.map((notification) => {
                return { ...notification, vehicleId: notification.deviceId };
              });
            })
          ),
        this.appSelector.getSortedPositions().pipe(
          filter((positions) => positions.length > 0),
          take(1)
        )
      ).pipe(
        map(
          ([vehicleNotifications, position]: [
            DSNotification[],
            Position[]
          ]) => {
            return this.mapVehicleNotifications(
              vehicleNotifications,
              position,
              vehicleId
            );
          }
        ),
        tap(this.addNewNotificationsPerVehicle)
      );
    }
  }

  mapVehicleNotifications = (
    vehicleNotifications: DSNotification[],
    position: Position[],
    vehicleId: string
  ) => {
    if (vehicleNotifications.length > 0) {
      const vehicle = position.find((pos) => {
        return pos.vehicle === vehicleNotifications[0].vehicleId.toString();
      });
      for (const vehicleNotification of vehicleNotifications) {
        vehicleNotification.content = `Pobranie opłaty. Saldo po pobraniu ${vehicleNotification.balance} PLN`;
        vehicleNotification.createdAt = moment(
          vehicleNotification.createdAt
        ).toDate();
        vehicleNotification.title = vehicle
          ? vehicle.name
          : this.decimalToHexString(Number(vehicleNotification.vehicleId));
      }
    }

    return { [vehicleId]: vehicleNotifications };
  }

  decimalToHexString = (decimalValue: number) => {
    if (decimalValue < 0) {
      decimalValue = 0xffff + decimalValue + 1;
    }

    return decimalValue.toString(16).toUpperCase();
  }

  loadUserNotifications(skip: number = 0, limit: number = 10): any {
    if (this.authService.isDSLocateUser()) {
      return combineLatest(
        this.http
          .get<UserNotifications>(
            this.dslocateUserNotificationsUrl + `?start=${skip}&count=${limit}`,
            {
              headers: this.authService.getJwtAuthHeaders(),
            }
          )
          .pipe(
            map((vehicleNotifications) => {
              vehicleNotifications = {
                notifications: vehicleNotifications.notifications.map(
                  (notification) => {
                    return {
                      ...notification,
                      vehicleId: notification.deviceId,
                    };
                  }
                ),
                unread: vehicleNotifications.unread,
              };
              return vehicleNotifications;
            })
          ),
        this.appSelector.getSortedPositions().pipe(
          filter((positions) => positions.length > 0),
          take(1)
        )
      ).pipe(map(this.mapUserNotifications), tap(this.assignUserNotifications));
    }
    return of();
  }

  mapUserNotifications = ([res, position]: [UserNotifications, Position[]]) => {
    return {
      notifications: [
        ...res.notifications.map((rowData) => {
          const vehicle = position.find((pos) => {
            return pos.vehicle === rowData.vehicleId.toString();
          });
          return {
            id: rowData.id,
            vehicleId: rowData.vehicleId,
            createdAt: moment(rowData.createdAt).toDate(),
            viewedAt: rowData.viewedAt
              ? moment(rowData.viewedAt).toDate()
              : undefined,
            content: rowData.content,
            title: vehicle
              ? vehicle.name
              : this.decimalToHexString(Number(rowData.vehicleId)),
          } as DSNotification;
        }),
      ],
      unread: res.unread,
    };
  }

  assignUserNotifications = (userNotifications: UserNotifications) => {
    const newNotifications = userNotifications.notifications.filter(
      (loadedNotification) =>
        !this.userNotifications.notifications.find(
          (notification) => notification.id === loadedNotification.id
        )
    );
    if (newNotifications && newNotifications.length > 0) {
      this.userNotifications = {
        notifications: [
          ...this.userNotifications.notifications,
          ...newNotifications,
        ].sort((a, b) =>
          a.createdAt.getTime() - b.createdAt.getTime() > 0
            ? -1
            : a.createdAt.getTime() - b.createdAt.getTime() === 0
            ? NotificationsService.sortById(a, b)
            : 1
        ),
        unread: userNotifications.unread,
      };
    } else {
      this.userNotifications = {
        notifications: this.userNotifications.notifications,
        unread: userNotifications.unread,
      };
    }
    this._notifications$.next(this.userNotifications);
  }

  markAllAsRead(): Observable<any> {
    if (this.authService.isDSLocateUser()) {
      return this.http
        .post(
          this.dslocateUserNotificationsUrl + '/markAll',
          {},
          {
            headers: this.authService.getJwtAuthHeaders(),
          }
        )
        .pipe(
          tap(() => {
            this.loadUserNotifications().subscribe();
          })
        );
    }
  }

  markAsRead(id: number): Observable<any> {
    if (this.authService.isDSLocateUser()) {
      return this.http
        .post(this.dslocateUserNotificationsUrl + '/mark', [id], {
          headers: this.authService.getJwtAuthHeaders(),
        })
        .pipe(
          tap(() => {
            const userNotification = this.userNotifications.notifications.find(
              (notification) => notification.id === id
            );
            if (userNotification) {
              userNotification.viewedAt = new Date();
              this.userNotifications = {
                notifications: [...this.userNotifications.notifications],
                unread: --this.userNotifications.unread,
              };
              this._notifications$.next(this.userNotifications);
            } else {
              // mark vehicleNotification as read
            }
          })
        );
    }
  }

  loadLastPerVehicleNotification(): Observable<{
    [key: string]: DSNotification[];
  }> {
    if (this.authService.isDSLocateUser()) {
      return combineLatest(
        this.http
          .get(this.deviceNotificationsUrl, {
            headers: this.authService.getJwtAuthHeaders(),
          })
          .pipe(map(this.mapDeviceToVehicle)),
        this.appSelector.getSortedPositions().pipe(
          filter((positions) => positions.length > 0),
          take(1)
        )
      ).pipe(
        map(this.mapVehiclesNotifications),
        tap(this.addNewNotificationsPerVehicle)
      );
    }
    return of();
  }
  mapDeviceToVehicle = (vehiclesNotifications: { [key: string]: any }) => {
    for (const vehicleNotifications of Object.values(vehiclesNotifications)) {
      if (vehicleNotifications.length > 0) {
        for (const vehicleNotification of vehicleNotifications) {
          vehicleNotification.vehicleId = vehicleNotification.deviceId;
        }
      }
    }
    return vehiclesNotifications;
  }

  mapVehiclesNotifications = ([vehiclesNotifications, position]: [
    { [key: string]: any },
    Position[]
  ]) => {
    for (const vehicleNotifications of Object.values(vehiclesNotifications)) {
      if (vehicleNotifications.length > 0) {
        const vehicle = position.find((pos) => {
          return pos.vehicle === vehicleNotifications[0].vehicleId.toString();
        });
        for (const vehicleNotification of vehicleNotifications) {
          vehicleNotification.content = `Pobranie opłaty. Saldo po pobraniu ${vehicleNotification.balance} PLN`;
          vehicleNotification.createdAt = moment(
            vehicleNotification.createdAt
          ).toDate();
          vehicleNotification.title = vehicle
            ? vehicle.name
            : this.decimalToHexString(Number(vehicleNotification.vehicleId));
        }
      }
    }
    return vehiclesNotifications;
  }

  addNewNotificationsPerVehicle = (
    vehiclesNotifications: VehiclesNotifications
  ) => {
    const newNotificationsPerVehicle = {};
    for (const [vehicleId, vehicleNotifications] of Object.entries(
      vehiclesNotifications
    )) {
      newNotificationsPerVehicle[vehicleId] = vehicleNotifications.filter(
        (loadedNotification) =>
          !this.notificationPerVehicle[vehicleId] ||
          !this.notificationPerVehicle[vehicleId].find(
            (notification) =>
              notification.createdAt.getTime() ===
              loadedNotification.createdAt.getTime()
          )
      );
    }
    for (const [vehicleId, newNotifications] of Object.entries(
      newNotificationsPerVehicle
    )) {
      this.notificationPerVehicle[vehicleId] = [
        ...(this.notificationPerVehicle[vehicleId] || []),
        ...(newNotifications as []),
      ].sort((a, b) =>
        a.createdAt.getTime() - b.createdAt.getTime() > 0
          ? -1
          : a.createdAt.getTime() - b.createdAt.getTime() === 0
          ? NotificationsService.sortById(a, b)
          : 1
      );
    }

    this._notificationsPerVehicle$.next(this.notificationPerVehicle);
  }

  getUserNotifications(): Observable<UserNotifications> {
    return this._notifications$.asObservable().pipe(distinctUntilChanged());
  }

  getNotificationPerVehicle(vehicleId: string): Observable<DSNotification[]> {
    return this._notificationsPerVehicle$.asObservable().pipe(
      map((notificationsPerVehicle) => notificationsPerVehicle[vehicleId]),
      filter((notifications) => {
        return notifications !== undefined;
      }),
      distinctUntilChanged()
    );
  }

  private static sortById(a: DSNotification, b: DSNotification): number {
    return a.id > b.id ? 1 : -1;
  }
}
